import * as React from "react";
import Icon from "../../Icon";
import { motion } from "framer-motion";
import AddToCalendar from "@culturehq/add-to-calendar";
import "@culturehq/add-to-calendar/dist/styles.css";
import { IParts } from "@src/components/Parts";
import DialogModal from "../../Dialog";
import LiveSchedules from "../../LiveSchedules";
import { GoogleMap, useLoadScript, MarkerF } from "@react-google-maps/api";
import Button from "@src/components/Button";
import formatDateWithOrdinal, {
  formatEventTimeWithTimeZoneLabel,
} from "@src/utils/dateFormatter";
import { useEventParts } from "@src/hooks/useEventParts";

const Parts: React.FC<IParts> = ({
  eventId,
  parts,
  eventPartsData,
  liveSchedules,
  eventTimezone,
  isInvitedToEvent,
}) => {
  const [openMap, setOpenMap] = React.useState<boolean>(false);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"],
  });

  const onLoadMarker = (marker: any) => {
    // console.log("Marker", marker?.position?.lat);
  };

  const handleViewDirections = ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`;
    window.open(directionsUrl, "_blank");
  };

  const handleFindHotelsNearby = ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    const hotelsUrl = `https://www.google.com/maps/search/hotels/@${latitude},${longitude},15z`;
    window.open(hotelsUrl, "_blank");
  };

  const { parts: derivedParts } = useEventParts({
    eventPartsData,
    liveSchedules,
    eventTimezone,
    isInvitedToEvent,
  });
  const visibleParts = parts ?? derivedParts;

  const addOneDay = (dateTime: string) => {
    var date = new Date(dateTime);
    date.setHours(date.getHours() + 24);
    var isoDate = date.toISOString();
    return isoDate;
  };

  return visibleParts.length > 0 ? (
    <div className={``}>
      {visibleParts.length === 0 ? (
        <></>
      ) : (
        <motion.div
          initial={{ opacity: 1 }}
          whileInView={{
            opacity: 1,
            transition: {
              delayChildren: 0.5,
              staggerChildren: 0.5,
            },
          }}
          className={`py-[5rem] lg:py-[10rem] bg-[rgba(42,46,50,1)]`}
        >
          <h3 className="font-andalusia text-4xl sm:text-5xl capitalize text-center text-[rgba(255,255,255,1)] mb-10 leading-loose">
            The Event Schedule
          </h3>
          <div className="flex flex-col items-center justify-center w-full">
            {/* <div className="hidden md:flex w-full max-w-6xl mx-auto justify-evenly items-center py-2 border-b border-y-white text-white font-eavesSmallCaps text-lg">
              <span className="flex-1 min-w-0 ml-[-1rem] mr-[1rem] text-center">
                Event
              </span>
              <span className="flex-1 min-w-0 ml-[1rem] mr-[1rem] text-center">
                Date
              </span>
              <span className="flex-1 min-w-0 mr-[0.5rem] text-center">
                Time
              </span>
              <span className="flex-1 min-w-0 ml-[1rem] text-center">
                Address
              </span>
              <span className="flex-1 min-w-0 ml-[1rem] text-center">
                Actions
              </span>
            </div> */}
            {visibleParts.map((item, idx) => (
              <motion.div
                initial={{ opacity: 0, y: -15 }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                  transition: {
                    delay: 0.2,
                    duration: 1,
                    ease: [0.79, 0.14, 0.15, 0.86],
                  },
                }}
                key={`home action ${idx}`}
                className={`flex flex-col lg:flex-row justify-center items-center gap-y-4 gap-x-5 w-full max-w-7xl mx-auto border-b ${
                  idx === 0 ? `border-t` : ``
                } border-y-[rgba(255,255,255,1)] py-10`}
              >
                <h1 className="flex-1 min-w-0 px-2 text-center font-brooklyn text-[rgba(255,255,255,1)] font-medium text-xl whitespace-normal break-words">
                  {item.name}
                </h1>
                <p className="flex-1 min-w-0 text-center text-[rgba(255,255,255,1)] font-brooklyn truncate">
                  {formatDateWithOrdinal(
                    item.time,
                    item.eventPartTimezone ?? eventTimezone
                  )}
                </p>
                <p className="flex-1 min-w-0 text-center font-brooklyn text-[rgba(255,255,255,1)]">
                  {formatEventTimeWithTimeZoneLabel(
                    item.time,
                    item.eventPartTimezone ?? eventTimezone
                  )}
                </p>
                {item.showAddress ? (
                  <p className="flex-1 min-w-0 max-w-sm sm:max-w-none text-center font-sans text-[rgba(255,255,255,1)] md:whitespace-normal">
                    {item?.address}
                  </p>
                ) : (
                  <></>
                )}
                <div className="flex-1 min-w-0 flex flex-col items-center gap-2">
                  {item.showAddress && (
                    <div className="w-[150px] flex justify-center border border-[rgba(255,255,255,1)]">
                      <AddToCalendar
                        event={{
                          name: item.name,
                          details: null,
                          location: item?.address,
                          startsAt: item?.time,
                          endsAt: addOneDay(item?.time),
                        }}
                      >
                        <span className="flex items-center justify-center h-[1.9rem] font-inter text-base text-[rgba(255,255,255,1)]">
                          Save Date
                        </span>
                      </AddToCalendar>
                    </div>
                  )}

                  {item.showAddress && item.hasLiveStream && liveSchedules && (
                    <DialogModal
                      fillScreen={true}
                      trigger={
                        <div className="w-[150px] h-[60px] flex items-center !text-[rgba(255,255,255,1)] justify-center border border-[rgba(255,255,255,1)]">
                          <span className="flex items-center justify-center h-[1.9rem] font-inter text-base !text-[rgba(255,255,255,1)] cursor-pointer">
                            Watch Live
                          </span>
                        </div>
                      }
                    >
                      <LiveSchedules schedules={liveSchedules} />
                    </DialogModal>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  ) : (
    <></>
  );
};

export default Parts;
